// Extracted from SphereTypeComposerOrganism.tsx:811-826 (EAINT-895) — the inline `<nav>` already
// did anchors + counts; this molecule replaces the counts with completion marks, the `adv` badge
// and the "Inherited & derived" entry. Relocated from `molecules/studio/ComposerIndexRailMolecule`
// (EAINT-921 Phase 2) and generalized for a second consumer (the Aspect Editor, Phase 4): the
// completion type now comes from `EditorBlockMolecule` (the one block molecule both editors
// render — the pre-rail `SphereTypeSectionMolecule` was relocated into it) rather than being
// duplicated, and every i18n key is under a `namespace`/`ariaLabelKey` pair the host supplies, so
// no `studio.typeEditor.*` literal is baked into this file.
// Considered: re-authoring — rejected per the spec's reuse audit (extract, don't re-author).

import React, { useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { EditorBlockCompletion } from '../EditorBlock/EditorBlockMolecule';
import IconOrGlyph from '../../atoms/Glyph/IconOrGlyph';
import './ComposerIndexRailMolecule.scss';

export interface ComposerIndexRailItem {
  id: string;
  /** A PrimeIcons class (`pi pi-tag`) or an Iconify name (`lucide:tag`). */
  icon: string;
  label: string;
  /**
   * Badged `adv` in the rail (`type-composer.jsx:867`, from `type-data.jsx:17`).
   * The badge is the rail's only signal that a section is advanced, so it says
   * so in words rather than by styling alone (ux-craft A6).
   */
  advanced?: boolean;
  completion: EditorBlockCompletion;
  /** How many rows the section holds, read beside the label. Omitted renders no count. */
  count?: number;
}

/** A labelled run of sections. The composer has one; the Aspect Editor has two — "The
 * definition" and "Reach & release". */
export interface ComposerIndexRailGroup {
  id: string;
  /** Already-localized heading. */
  heading: string;
  items: ComposerIndexRailItem[];
}

export interface ComposerIndexRailMoleculeProps {
  /** One group under the rail's own "Sections" heading. Ignored when `groups` is set. */
  items?: ComposerIndexRailItem[];
  /** Several labelled groups, in order. Replaces `items` and the built-in heading. */
  groups?: ComposerIndexRailGroup[];
  /**
   * Fired after an item scrolls its section into view — the rail pattern selects the block it
   * lands on, and an entry with no section in the document (a rail-only view) is reached through
   * this alone.
   */
  onItemActivate?: (sectionId: string) => void;
  /**
   * Replaces the computed closing note. A host whose note is not about proposals and checks says
   * its own thing here.
   */
  note?: React.ReactNode;
  /**
   * The note that closes the rail (`type-composer.jsx:877-883`). Platform types
   * say why nothing is editable; an editable type reports what is still open
   * before publish, so the rail doubles as progress rather than just navigation.
   */
  locked?: boolean;
  openCheckCount?: number;
  pendingGhostCount?: number;
  /** Proposals kept in this session — the numerator of the note's "N of M ... kept." reading. */
  keptGhostCount?: number;
  /** DOM id each item's anchor points at, e.g. `sphere-type-section-identity`. */
  anchorId: (sectionId: string) => string;
  /** Section id currently in view, for active-item styling. */
  activeSectionId?: string;
  /** Item count shown on the "Inherited & derived" entry (Health · Presentation · the parent's contribution). */
  inheritCount?: number;
  /** Links to the side rail's Inherited tab — this is a rail entry, not a thirteenth section.
   * Omitted, the entry and its heading are not rendered: a host with nothing inherited to show
   * offers no route to it. */
  onInheritedClick?: () => void;
  /** i18n namespace for this rail's own strings (heading, adv badge, note, inherited entry). The
   * host supplies it — e.g. `'studio.typeEditor.composer.indexRail'` — so no namespace is baked
   * into this file. */
  namespace: string;
  /** Full i18n key for the rail's `aria-label`. Kept separate from `namespace` because the
   * prototype's own key (`studio.typeEditor.sectionIndexLabel`) sits outside the `indexRail`
   * namespace; a second host supplies its own key either way. */
  ariaLabelKey: string;
}

const ComposerIndexRailMolecule: React.FC<ComposerIndexRailMoleculeProps> = ({
  items = [],
  groups,
  onItemActivate,
  note,
  locked,
  openCheckCount,
  pendingGhostCount,
  keptGhostCount,
  anchorId,
  activeSectionId,
  inheritCount,
  onInheritedClick,
  namespace,
  ariaLabelKey,
}) => {
  const { t } = useTranslation();
  // Roving tabindex (WCAG 2.1.1 / A1): one stop in Tab order for the whole
  // rail, arrows move focus within it. Items + the trailing Inherited button
  // form one flat group of `items.length + 1` stops.
  const sections: ComposerIndexRailGroup[] = groups ?? [
    { id: 'sections', heading: t(`${namespace}.sectionsHeading`), items },
  ];
  const flatItems = sections.flatMap((group) => group.items);
  const hasInherited = Boolean(onInheritedClick);
  const stopCount = flatItems.length + (hasInherited ? 1 : 0);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const stopRefs = useRef<Array<HTMLElement | null>>([]);

  const focusStop = (index: number): void => {
    const wrapped = (index + stopCount) % stopCount;
    setFocusedIndex(wrapped);
    stopRefs.current[wrapped]?.focus();
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusStop(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusStop(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusStop(0);
        break;
      case 'End':
        event.preventDefault();
        focusStop(stopCount - 1);
        break;
      default:
        break;
    }
  };

  /**
   * Scrolls the section into view and moves focus to it, so keyboard and screen
   * reader users land where sighted users look. `scrollIntoView` is a no-op in
   * jsdom, which is fine — the assertion that matters is that focus moved.
   */
  const scrollToSection = (sectionId: string) => {
    const el = typeof document === 'undefined' ? null : document.getElementById(anchorId(sectionId));
    if (!el) return;
    // jsdom does not implement scrollIntoView; the focus move below is the
    // part that must work everywhere, so this stays optional.
    el.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    // Sections are not natively focusable; -1 makes them programmatically so
    // without adding them to the tab order.
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  };

  return (
    <nav
      className="composer-index-rail"
      aria-label={t(ariaLabelKey)}
      data-testid="composer-index-rail"
      data-container="index"
    >
      {/*
        NOT `href="#<anchor>"`. The app mounts a HashRouter, so a fragment href
        REPLACES the route hash — `#/studio/types/:id` becomes
        `#sphere-type-section-identity`, which matches no route and renders a
        blank page. (Reported from the running app; no test caught it because the
        organism's tests mount it without a router.) A button that scrolls and
        moves focus is also what the spec's keyboard map asks for: "Enter scrolls
        to section and moves focus to its heading".
      */}
      {sections.map((group, groupIndex) => {
        const offset = sections.slice(0, groupIndex).reduce((n, g) => n + g.items.length, 0);
        return (
          <React.Fragment key={group.id}>
            <span
              className={`composer-index-rail__heading${groupIndex > 0 ? ' composer-index-rail__heading--group' : ''}`}
              data-datum={groupIndex === 0 ? 'index.sections' : undefined}
              data-kind={groupIndex === 0 ? 'heading' : undefined}
            >
              {group.heading}
            </span>
            {group.items.map((item, itemIndex) => {
              const index = offset + itemIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  ref={(node) => {
                    stopRefs.current[index] = node;
                  }}
                  className={`composer-index-rail__item${item.id === activeSectionId ? ' composer-index-rail__item--active' : ''}`}
                  aria-current={item.id === activeSectionId ? 'true' : undefined}
                  tabIndex={index === focusedIndex ? 0 : -1}
                  onFocus={() => setFocusedIndex(index)}
                  onKeyDown={handleKeyDown(index)}
                  onClick={() => {
                    scrollToSection(item.id);
                    onItemActivate?.(item.id);
                  }}
                  data-rail-entry="section"
                  data-datum="index.nav-item"
                  data-kind="nav-item"
                  data-container="index > each(TYPE_BLOCKS.filter(b => !b.side))"
                >
                  <IconOrGlyph icon={item.icon} className="composer-index-rail__icon" />
                  <span>{item.label}</span>
                  {item.advanced && (
                    <span
                      className="composer-index-rail__adv"
                      title={t(`${namespace}.advancedTitle`)}
                      data-datum="index.adv-badge"
                      data-kind="badge"
                      data-container="index > each(TYPE_BLOCKS.filter(b => !b.side))"
                    >
                      {t(`${namespace}.advancedBadge`)}
                    </span>
                  )}
                  {item.count !== undefined && <span className="composer-index-rail__count">{item.count}</span>}
                  <span
                    className={`composer-index-rail__completion composer-index-rail__completion--${item.completion}`}
                    /* `type-composer.jsx:861` — the mark's meaning, which a shape alone
                       cannot carry (ux-craft A6: colour/shape is never the sole signal). */
                    title={
                      item.completion === 'ghost'
                        ? t(`${namespace}.markGhostTitle`)
                        : item.completion === 'empty'
                          ? t(`${namespace}.markEmptyTitle`)
                          : undefined
                    }
                    data-testid={`composer-index-rail-completion-${item.id}-${item.completion}`}
                    data-datum="index.status-mark"
                    data-kind="status-mark"
                    data-container="index > each(TYPE_BLOCKS.filter(b => !b.side))"
                  >
                    {/* `studio.css:483-485` (`.t-mark`) — the circle itself carries done/ghost;
                        empty renders as a bare ring, no glyph inside. */}
                    {item.completion !== 'empty' && (
                      <i
                        className={item.completion === 'ghost' ? 'pi pi-sparkles' : 'pi pi-check'}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </React.Fragment>
        );
      })}

      {hasInherited && (
        <>
          <span
            className="composer-index-rail__heading composer-index-rail__heading--inherited"
            data-datum="index.inherited-amp-derived"
            data-kind="heading"
          >
            {t(`${namespace}.inheritedHeading`)}
          </span>
          <button
            type="button"
            ref={(node) => {
              stopRefs.current[flatItems.length] = node;
            }}
            className="composer-index-rail__item composer-index-rail__item--button"
            onClick={onInheritedClick}
            tabIndex={flatItems.length === focusedIndex ? 0 : -1}
            onFocus={() => setFocusedIndex(flatItems.length)}
            onKeyDown={handleKeyDown(flatItems.length)}
            data-testid="composer-index-rail-inherited"
            data-datum="index.work-health-presentation"
            data-kind="nav-item"
          >
            {/*
              `type-composer.jsx:871-872` — the inherited entry's own mark, always
              "done" (a git-merge glyph): inherited content is never ghost/empty,
              it just is. Distinct from `inheritCount`, which is additional UI with
              no contract datum of its own.
            */}
            <span
              className="composer-index-rail__completion composer-index-rail__completion--done"
              data-datum="index.status-mark#2"
              data-kind="status-mark"
            >
              <i className="pi pi-sitemap" aria-hidden="true" />
            </span>
            <span>{t(`${namespace}.inheritedEntry`)}</span>
            {inheritCount !== undefined && <span className="composer-index-rail__count">{inheritCount}</span>}
          </button>
        </>
      )}
      {/*
        The rail closes with where you stand, not just where you can go
        (`type-composer.jsx:877-883`). A platform type says why nothing is
        editable; an editable one says what is still open before publish.
      */}
      <p
        className="composer-index-rail__note"
        data-testid="composer-index-rail-note"
        data-datum="index.platform-owned-read-only-here"
        data-kind="note"
      >
        {note !== undefined ? (
          note
        ) : locked ? (
          t(`${namespace}.platformOwnedNote`)
        ) : (
          <>
            {/*
              `type-composer.jsx:879-882`: with proposals in play the note counts how many have
              been kept, and says so ONLY then — "No proposals waiting." is the other branch, not
              a prefix to the count. Rendering both (the count then "No proposals waiting.") said
              two contradictory things at once.
            */}
            {pendingGhostCount || keptGhostCount ? (
              <>
                <strong data-datum="index.of" data-kind="strong">
                  {t(`${namespace}.proposalsKeptCount`, {
                    kept: keptGhostCount ?? 0,
                    total: (keptGhostCount ?? 0) + (pendingGhostCount ?? 0),
                  })}
                </strong>{' '}
                {t(`${namespace}.proposalsKeptRest`)}{' '}
              </>
            ) : (
              <>{t(`${namespace}.noProposals`)} </>
            )}
            {openCheckCount === undefined
              ? null
              : openCheckCount > 0
                ? t(`${namespace}.checksLeft`, { count: openCheckCount })
                : t(`${namespace}.allChecksPass`)}
          </>
        )}
      </p>
    </nav>
  );
};

ComposerIndexRailMolecule.displayName = 'ComposerIndexRailMolecule';

export default ComposerIndexRailMolecule;
